import { useEffect, useMemo, useState, useRef, type ReactNode } from "react";
import { useApp } from "../../context/AppContext";
import type { Person, PlayCategory, TriviaQuestion } from "../../models/types";
import { signedPostScore } from "../../api/client";
import { getDeviceId } from "../../storage/device";
import {
  isSundiusCousin,
  paddedDurationMs,
  shuffle,
  uuid,
} from "../../utils/calendar";
import { hapticLight, hapticSuccess, hapticError } from "../../utils/haptics";
import { Avatar } from "../shared/Avatar";
import { IconFlame, IconParty, IconTrophy, IconWave } from "../shared/Icons";
import { GameResults, type GameResultState } from "./GameResults";
import { LeaderboardPanel } from "./LeaderboardPanel";
import { CousinsKidsGame } from "./CousinsKidsGame";
import { LearnFlowHeader } from "./LearnFlowHeader";

type GameId =
  | "flash_easy"
  | "flash_medium"
  | "flash_everyone"
  | "family_match"
  | "whos_with_who"
  | "birth_order_easy"
  | "birth_order_medium"
  | "birth_order_ultra"
  | "family_lore"
  | "leaderboard";

const CATEGORIES: {
  id: PlayCategory;
  title: string;
  tagline: string;
  accent: "meet" | "bring" | "trivia";
}[] = [
  {
    id: "meetTheFam",
    title: "Meet the Fam",
    tagline: "Faces, branches & cousin names",
    accent: "meet",
  },
  {
    id: "bringItOn",
    title: "Bring It On",
    tagline: "Name kids, sort by age & prove it",
    accent: "bring",
  },
  {
    id: "triviaNight",
    title: "Trivia Night!",
    tagline: "Family lore & quiz games",
    accent: "trivia",
  },
];

function categoryGames(category: PlayCategory): { id: GameId; title: string; subtitle: string; mode: string }[] {
  switch (category) {
    case "meetTheFam":
      return [
        { id: "flash_easy", title: "Aunts & Uncles", subtitle: "Flashcards", mode: "easy" },
        { id: "flash_medium", title: "All the Cousins", subtitle: "Flashcards", mode: "medium" },
        { id: "family_match", title: "Family Match", subtitle: "Pair the branches", mode: "family_match" },
        { id: "whos_with_who", title: "Who's With Who", subtitle: "Couples & partners", mode: "whos_with_who" },
      ];
    case "bringItOn":
      return [
        { id: "birth_order_easy", title: "Birth Order · Aunts & Uncles", subtitle: "Oldest to youngest", mode: "birth_order_easy" },
        { id: "birth_order_medium", title: "Birth Order · Cousins", subtitle: "Oldest to youngest", mode: "birth_order_medium" },
        { id: "birth_order_ultra", title: "Cousins' Kids", subtitle: "Name them, their parents, then sort by age", mode: "birth_order_ultra" },
        { id: "flash_everyone", title: "Final test", subtitle: "Everyone flashcards", mode: "everyone" },
      ];
    case "triviaNight":
      return [{ id: "family_lore", title: "Family Lore", subtitle: "Trivia from the bundle", mode: "family_lore" }];
    default:
      return [];
  }
}

async function submitScore(
  session: NonNullable<ReturnType<typeof useApp>["session"]>,
  displayName: string,
  personId: string,
  mode: string,
  score: number,
  total: number,
  startedAt: number,
) {
  const rawDuration = Date.now() - startedAt;
  const duration_ms = paddedDurationMs(rawDuration, total, mode);
  await signedPostScore(session, {
    entry_id: uuid().replace(/-/g, "").slice(0, 32),
    person_id: personId,
    device_id: getDeviceId(),
    display_name: displayName,
    mode,
    score,
    total,
    duration_ms,
    completed_at: Math.floor(Date.now() / 1000),
  });
}

function FlashcardGame({
  deck,
  roster,
  onDone,
}: {
  /** Pre-shuffled deck from the parent — never reshuffled here. */
  deck: Person[];
  /** Full people list for partner lookup. */
  roster: Person[];
  onDone: (score: number, total: number, startedAt: number) => void;
}) {
  const peopleById = useMemo(() => {
    const map = new Map<string, Person>();
    for (const p of roster) map.set(p.person_id, p);
    return map;
  }, [roster]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [score, setScore] = useState(0);
  const [misses, setMisses] = useState<Person[]>([]);
  const [reviewingMisses, setReviewingMisses] = useState(false);
  const [startedAt] = useState(Date.now());
  const [finished, setFinished] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  // Block re-entrant advances (duplicate pointer/click events were burning
  // through the whole deck in one gesture).
  const advanceLock = useRef(false);
  const dragRef = useRef<{ pointerId: number | null; startX: number; x: number }>({
    pointerId: null,
    startX: 0,
    x: 0,
  });
  const [dragX, setDragX] = useState(0);

  useEffect(() => {
    if (index >= deck.length && !finished && !reviewingMisses) {
      if (misses.length > 0) {
        setReviewingMisses(true);
        setIndex(0);
        return;
      }
      setFinished(true);
      onDoneRef.current(score, deck.length, startedAt);
    }
  }, [index, deck.length, finished, score, startedAt, misses.length, reviewingMisses]);

  const activeDeck = reviewingMisses ? misses : deck;

  if (index >= activeDeck.length && finished) {
    return <p className="muted pad-screen">Wrapping up…</p>;
  }

  const card = activeDeck[index];
  if (!card) return null;

  const partner = card.spouse_ids
    .map((id) => peopleById.get(id))
    .find(Boolean);

  const advance = (gotIt: boolean) => {
    if (finished || advanceLock.current) return;
    advanceLock.current = true;
    if (gotIt) {
      hapticSuccess();
      setScore((s) => s + 1);
    } else {
      hapticError();
      if (!reviewingMisses) setMisses((m) => [...m, card]);
    }
    setFlipped(false);
    setDragX(0);
    dragRef.current = { pointerId: null, startX: 0, x: 0 };
    if (index + 1 >= activeDeck.length && reviewingMisses) {
      setFinished(true);
      onDoneRef.current(score + (gotIt ? 1 : 0), deck.length, startedAt);
      return;
    }
    setIndex((i) => i + 1);
    window.setTimeout(() => {
      advanceLock.current = false;
    }, 280);
  };

  return (
    <div className="stack pad-screen">
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${((index + 1) / Math.max(activeDeck.length, 1)) * 100}%` }}
        />
      </div>
      <p className="muted">
        {reviewingMisses ? "Review misses · " : ""}
        {index + 1} / {activeDeck.length}
      </p>
      <div
        className="flashcard"
        style={{
          transform: `translateX(${dragX}px) rotate(${dragX * 0.03}deg)`,
          borderColor:
            dragX > 28 ? "var(--success)" : dragX < -28 ? "var(--review)" : undefined,
          touchAction: "pan-y",
        }}
        onClick={() => {
          if (Math.abs(dragRef.current.x) > 10) return;
          setFlipped((f) => !f);
        }}
        onPointerDown={(e) => {
          if (dragRef.current.pointerId !== null) return;
          dragRef.current = { pointerId: e.pointerId, startX: e.clientX, x: 0 };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (dragRef.current.pointerId !== e.pointerId) return;
          const x = e.clientX - dragRef.current.startX;
          dragRef.current.x = x;
          setDragX(x);
        }}
        onPointerUp={(e) => {
          if (dragRef.current.pointerId !== e.pointerId) return;
          const x = dragRef.current.x;
          dragRef.current = { pointerId: null, startX: 0, x: 0 };
          try {
            e.currentTarget.releasePointerCapture(e.pointerId);
          } catch {
            /* already released */
          }
          if (x > 72) advance(true);
          else if (x < -72) advance(false);
          else setDragX(0);
        }}
        onPointerCancel={() => {
          dragRef.current = { pointerId: null, startX: 0, x: 0 };
          setDragX(0);
        }}
      >
        <Avatar key={card.person_id} person={card} size={160} ring />
        <h2 className="title-serif" style={{ marginTop: 16 }}>
          {flipped ? card.display_name : "Who is this?"}
        </h2>
        {flipped && partner ? <p className="muted">With {partner.display_name}</p> : null}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <button type="button" className="btn-primary" onClick={() => advance(true)}>
          Got it
        </button>
        <button type="button" className="btn-ghost" onClick={() => advance(false)}>
          Skip
        </button>
      </div>
    </div>
  );
}

function TriviaGame({
  questions,
  onDone,
}: {
  questions: TriviaQuestion[];
  onDone: (score: number, total: number, startedAt: number) => void;
}) {
  const [deck] = useState(() => shuffle(questions));
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(2);
  const [startedAt] = useState(Date.now());
  const [done, setDone] = useState(false);
  const [picked, setPicked] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const q = deck[index];

  useEffect(() => {
    if ((index >= deck.length || lives <= 0) && !done) {
      setDone(true);
      onDoneRef.current(score, deck.length, startedAt);
    }
  }, [index, deck.length, done, score, startedAt, lives]);

  if (index >= deck.length || lives <= 0) return <p className="muted pad-screen">Wrapping up…</p>;
  if (!q) return null;

  const pick = (i: number) => {
    if (revealed) return;
    hapticLight();
    setPicked(i);
    setRevealed(true);
    const correct = i === q.correct_index;
    if (correct) setScore((s) => s + 1);
    else setLives((l) => l - 1);
    window.setTimeout(() => {
      setPicked(null);
      setRevealed(false);
      setIndex((x) => x + 1);
    }, correct ? 900 : 1400);
  };

  return (
    <div className="stack pad-screen">
      <div className="game-status">
        <span>{index + 1} / {deck.length}</span>
        <span>{"❤".repeat(lives)}{"🤍".repeat(Math.max(0, 2 - lives))}</span>
      </div>
      {q.setup ? <p>{q.setup}</p> : null}
      <h2 className="title-serif">{q.question}</h2>
      <div className="stack">
        {q.choices.map((choice, i) => {
          let cls = "btn-primary";
          if (revealed && i === q.correct_index) cls += " trivia-correct";
          else if (revealed && picked === i && i !== q.correct_index) cls += " trivia-wrong";
          return (
            <button key={choice} type="button" className={cls} onClick={() => pick(i)}>
              {choice}
            </button>
          );
        })}
      </div>
      {revealed && picked !== null && picked !== q.correct_index && q.wrong_answer_message ? (
        <p className="error-text">{q.wrong_answer_message}</p>
      ) : null}
      {revealed && q.answer_detail ? <p className="muted">{q.answer_detail}</p> : null}
    </div>
  );
}

function BirthOrderGame({
  people,
  onDone,
}: {
  people: Person[];
  onDone: (score: number, total: number, startedAt: number) => void;
}) {
  const sorted = useMemo(
    () =>
      [...people].sort((a, b) => {
        const ay = a.birth_year ?? 9999;
        const by = b.birth_year ?? 9999;
        if (ay !== by) return ay - by;
        return (a.birth_day ?? 1) - (b.birth_day ?? 1);
      }),
    [people],
  );
  const [order, setOrder] = useState(() => shuffle(people));
  const [startedAt] = useState(Date.now());
  const [submitted, setSubmitted] = useState(false);
  const [reported, setReported] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!submitted || reported) return;
    let score = 0;
    for (let i = 0; i < sorted.length; i += 1) {
      if (sorted[i].person_id === order[i]?.person_id) score += 1;
    }
    setReported(true);
    onDoneRef.current(score, sorted.length, startedAt);
  }, [submitted, reported, sorted, order, startedAt]);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length) return;
    setOrder((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  if (submitted) {
    return <p className="muted pad-screen">Wrapping up…</p>;
  }

  return (
    <div className="stack pad-screen">
      <p className="muted">Drag oldest → youngest using arrows</p>
      {order.map((person, i) => (
        <div key={person.person_id} className="card pad-screen" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Avatar person={person} size={48} />
          <div style={{ flex: 1 }}>{person.display_name}</div>
          <button type="button" className="btn-ghost" onClick={() => move(i, i - 1)}>↑</button>
          <button type="button" className="btn-ghost" onClick={() => move(i, i + 1)}>↓</button>
        </div>
      ))}
      <button type="button" className="btn-primary" onClick={() => setSubmitted(true)}>
        Submit order
      </button>
    </div>
  );
}

function MatchGame({
  people,
  onDone,
}: {
  people: Person[];
  onDone: (score: number, total: number, startedAt: number) => void;
}) {
  const pairs = useMemo(() => {
    const cousins = people.filter(isSundiusCousin);
    return cousins.map((c) => ({
      cousin: c,
      parent: people.find((p) => c.parent_ids.includes(p.person_id) && p.generation === 1),
    })).filter((p) => p.parent);
  }, [people]);
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [startedAt] = useState(Date.now());
  const [done, setDone] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const current = pairs[index];

  const choices = useMemo(() => {
    if (!current?.parent) return [];
    const parent = current.parent;
    const distractors = shuffle(
      people.filter((p) => p.generation === 1 && p.person_id !== parent.person_id),
    ).slice(0, 3);
    return shuffle([parent, ...distractors]);
  }, [current, people]);

  useEffect(() => {
    if (index >= pairs.length && !done) {
      setDone(true);
      onDoneRef.current(score, pairs.length, startedAt);
    }
  }, [index, pairs.length, done, score, startedAt]);

  if (index >= pairs.length) return <p className="muted pad-screen">Wrapping up…</p>;
  if (!current) return null;

  return (
    <div className="stack pad-screen">
      <Avatar person={current.cousin} size={120} ring />
      <h2 className="title-serif">Which aunt/uncle is {current.cousin.display_name}&apos;s parent?</h2>
      {choices.map((choice) => (
        <button
          key={choice.person_id}
          type="button"
          className="btn-primary"
          onClick={() => {
            if (choice.person_id === current.parent?.person_id) setScore((s) => s + 1);
            setIndex((i) => i + 1);
          }}
        >
          {choice.display_name}
        </button>
      ))}
    </div>
  );
}

function WhosWithWhoGame({
  people,
  onDone,
}: {
  people: Person[];
  onDone: (score: number, total: number, startedAt: number) => void;
}) {
  const pairs = useMemo(
    () =>
      people.filter((p) => p.generation <= 2 && !p.is_partner && p.spouse_ids.length > 0).slice(0, 12),
    [people],
  );
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [startedAt] = useState(Date.now());
  const [done, setDone] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const current = pairs[index];

  const choices = useMemo(() => {
    if (!current) return [];
    const partnerId = current.spouse_ids[0];
    const partner = people.find((p) => p.person_id === partnerId);
    const distractors = shuffle(
      people.filter(
        (p) => p.person_id !== partnerId && p.generation === current.generation,
      ),
    ).slice(0, 3);
    return shuffle(partner ? [partner, ...distractors] : distractors);
  }, [current, people]);

  useEffect(() => {
    if (index >= pairs.length && !done) {
      setDone(true);
      onDoneRef.current(score, pairs.length, startedAt);
    }
  }, [index, pairs.length, done, score, startedAt]);

  if (index >= pairs.length) return <p className="muted pad-screen">Wrapping up…</p>;
  if (!current) return null;

  const partnerId = current.spouse_ids[0];

  return (
    <div className="stack pad-screen">
      <Avatar person={current} size={120} ring />
      <h2 className="title-serif">Who is with {current.display_name}?</h2>
      {choices.map((choice) => (
        <button
          key={choice.person_id}
          type="button"
          className="btn-primary"
          onClick={() => {
            if (choice.person_id === partnerId) setScore((s) => s + 1);
            setIndex((i) => i + 1);
          }}
        >
          {choice.display_name}
        </button>
      ))}
    </div>
  );
}

export function PlayView({
  forceLeaderboard = false,
  onCloseLeaderboard,
}: {
  forceLeaderboard?: boolean;
  onCloseLeaderboard?: () => void;
} = {}) {
  const { bundle, session, refreshLeaderboard, setPlayInGame, leaderboardByMode, me } = useApp();
  const [category, setCategory] = useState<PlayCategory | null>(null);
  const [activeGame, setActiveGame] = useState<{ id: GameId; mode: string } | null>(null);
  const [flashDeck, setFlashDeck] = useState<Person[] | null>(null);
  const [result, setResult] = useState<GameResultState | null>(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardFocusMode, setLeaderboardFocusMode] = useState<string | undefined>();
  const [gameNonce, setGameNonce] = useState(0);

  useEffect(() => {
    const inGame = !!activeGame && activeGame.id !== "leaderboard";
    setPlayInGame(inGame);
  }, [activeGame, setPlayInGame]);

  useEffect(() => {
    return () => setPlayInGame(false);
  }, [setPlayInGame]);

  const gameSubtitle = useMemo(() => {
    if (!activeGame) return "";
    const all = [
      ...categoryGames("meetTheFam"),
      ...categoryGames("bringItOn"),
      ...categoryGames("triviaNight"),
    ];
    return all.find((g) => g.id === activeGame.id)?.title ?? "Game";
  }, [activeGame]);

  const rankHint = useMemo(() => {
    if (!me || !activeGame) return null;
    const board = leaderboardByMode[activeGame.mode] ?? [];
    return board.find((e) => e.person_id === me.person_id)?.rank ?? null;
  }, [activeGame, leaderboardByMode, me]);

  const myOverallRank = useMemo(() => {
    if (!me) return null;
    const overall = leaderboardByMode.overall ?? [];
    return overall.find((e) => e.person_id === me.person_id)?.rank ?? null;
  }, [leaderboardByMode, me]);
  if (!bundle || !session) return null;
  const people = bundle.people;

  const flashPeopleFor = (mode: string) => {
    if (mode === "easy") return people.filter((p) => bundle.meta.sibling_order.includes(p.person_id));
    if (mode === "medium") return people.filter(isSundiusCousin);
    return people.filter((p) => p.generation >= 1 && p.generation <= 2 && !p.is_deceased && !p.is_expected);
  };

  const birthOrderPeople = (mode: string) => {
    if (mode === "birth_order_easy") return flashPeopleFor("easy");
    if (mode === "birth_order_medium") return flashPeopleFor("medium");
    return people.filter((p) => p.generation === 3);
  };

  const startGame = (id: GameId, mode: string) => {
    if (id.startsWith("flash_")) {
      setFlashDeck(shuffle(flashPeopleFor(mode)));
    } else {
      setFlashDeck(null);
    }
    setResult(null);
    setGameNonce((n) => n + 1);
    setActiveGame({ id, mode });
  };

  const finishGame = async (mode: string, score: number, total: number, startedAt: number) => {
    const rawDuration = Date.now() - startedAt;
    const durationMs = paddedDurationMs(rawDuration, total, mode);
    let rank: number | null = null;
    let error: string | null = null;
    try {
      await submitScore(session, session.display_name, session.person_id, mode, score, total, startedAt);
      const board = (await refreshLeaderboard(mode)) ?? [];
      rank = board.find((e) => e.person_id === session.person_id)?.rank ?? null;
    } catch (e) {
      error = e instanceof Error ? e.message : "Could not save score";
    }
    setResult({ mode, score, total, durationMs, rank, error });
  };

  const openLeaderboard = (mode?: string) => {
    setLeaderboardFocusMode(mode);
    setShowLeaderboard(true);
  };

  if (forceLeaderboard || showLeaderboard || activeGame?.id === "leaderboard") {
    return (
      <div className="pad-screen">
        <LeaderboardPanel
          initialMode={leaderboardFocusMode ?? (forceLeaderboard ? undefined : result?.mode)}
          onDone={() => {
            if (forceLeaderboard) {
              onCloseLeaderboard?.();
            } else {
              setShowLeaderboard(false);
              setLeaderboardFocusMode(undefined);
            }
          }}
        />
      </div>
    );
  }

  if (result) {
    return (
      <GameResults
        result={result}
        onPlayAgain={() => {
          const mode = result.mode;
          const map: Record<string, GameId> = {
            easy: "flash_easy",
            medium: "flash_medium",
            everyone: "flash_everyone",
            family_match: "family_match",
            whos_with_who: "whos_with_who",
            birth_order_easy: "birth_order_easy",
            birth_order_medium: "birth_order_medium",
            birth_order_ultra: "birth_order_ultra",
            family_lore: "family_lore",
          };
          const id = activeGame?.id ?? map[mode] ?? "flash_easy";
          startGame(id, mode);
        }}
        onBackToGames={() => {
          setResult(null);
          setActiveGame(null);
          setFlashDeck(null);
          setCategory(null);
        }}
        onSeeRankings={() => {
          openLeaderboard(result.mode);
        }}
      />
    );
  }

  if (activeGame) {
    const { id, mode } = activeGame;
    const header = (
      <LearnFlowHeader
        subtitle={gameSubtitle}
        rankHint={rankHint}
        onBack={() => {
          setActiveGame(null);
          setFlashDeck(null);
        }}
        onLeaderboard={() => openLeaderboard(mode)}
      />
    );
    const wrap = (body: ReactNode) => (
      <div key={gameNonce}>
        {header}
        {body}
      </div>
    );
    if (id.startsWith("flash_")) {
      if (!flashDeck || flashDeck.length === 0) {
        return wrap(<p className="muted pad-screen">No faces in this deck.</p>);
      }
      return wrap(
        <FlashcardGame
          deck={flashDeck}
          roster={people}
          onDone={(s, t, st) => void finishGame(mode, s, t, st)}
        />,
      );
    }
    if (id === "family_lore") {
      return wrap(
        <TriviaGame
          questions={bundle.trivia_questions}
          onDone={(s, t, st) => void finishGame(mode, s, t, st)}
        />,
      );
    }
    if (id === "birth_order_ultra") {
      return wrap(
        <CousinsKidsGame
          people={people}
          onExit={() => setActiveGame(null)}
          onDone={(s, t, st) => void finishGame(mode, s, t, st)}
        />,
      );
    }
    if (id.startsWith("birth_order_")) {
      return wrap(
        <BirthOrderGame
          people={birthOrderPeople(mode)}
          onDone={(s, t, st) => void finishGame(mode, s, t, st)}
        />,
      );
    }
    if (id === "family_match") {
      return wrap(<MatchGame people={people} onDone={(s, t, st) => void finishGame(mode, s, t, st)} />);
    }
    if (id === "whos_with_who") {
      return wrap(<WhosWithWhoGame people={people} onDone={(s, t, st) => void finishGame(mode, s, t, st)} />);
    }
  }

  if (category) {
    const games = categoryGames(category);
    return (
      <div className="pad-screen stack">
        <button type="button" className="btn-ghost" onClick={() => setCategory(null)}>
          ← Categories
        </button>
        {games.map((game) => (
          <button
            key={game.id}
            type="button"
            className="game-row"
            onClick={() => startGame(game.id, game.mode)}
          >
            <span className="icon-well lg" aria-hidden>
              ★
            </span>
            <div style={{ flex: 1 }}>
              <strong>{game.title}</strong>
              <div className="sub">{game.subtitle}</div>
            </div>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="pad-screen stack">
      <p className="play-intro">How well do you know the Sundius crew? Pick your vibe.</p>
      {myOverallRank ? (
        <button type="button" className="action-banner" onClick={() => openLeaderboard()}>
          <span className="icon-well lg"><IconTrophy /></span>
          <div className="copy">
            <strong>Family Showdown · #{myOverallRank}</strong>
            <div className="sub">See the full leaderboard</div>
          </div>
          <span className="chevron">›</span>
        </button>
      ) : null}
      {CATEGORIES.map((cat) => {
        const count = categoryGames(cat.id).length;
        return (
          <button
            key={cat.id}
            type="button"
            className={`category-card ${cat.accent}`}
            onClick={() => setCategory(cat.id)}
          >
            <span className="icon-well xl" aria-hidden>
              {cat.accent === "meet" ? (
                <IconWave />
              ) : cat.accent === "bring" ? (
                <IconFlame />
              ) : (
                <IconParty />
              )}
            </span>
            <div className="copy">
              <strong>{cat.title}</strong>
              <div className="sub">{cat.tagline}</div>
            </div>
            <span className="game-count">
              {count} {count === 1 ? "game" : "games"}
            </span>
          </button>
        );
      })}
    </div>
  );
}
